/**
 * Client-side localStorage cache for upcoming events.
 * Enables stale-while-revalidate: show cached data instantly,
 * then do a cheap hash check to determine if a full refetch is needed.
 */

const CACHE_PREFIX = "as:events:";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface CachedEvents {
  events: unknown[];
  hash: string;
  cachedAt: number;
}

/** Read cached events for an org. Returns null if missing or expired. */
export function getCachedEvents(orgId: string): CachedEvents | null {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${orgId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedEvents;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(`${CACHE_PREFIX}${orgId}`);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Write events to the localStorage cache. */
export function setCachedEvents(
  orgId: string,
  events: unknown[],
  hash: string,
): void {
  try {
    const entry: CachedEvents = { events, hash, cachedAt: Date.now() };
    localStorage.setItem(`${CACHE_PREFIX}${orgId}`, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

/** Clear cached events for an org. */
export function clearCachedEvents(orgId: string): void {
  try {
    localStorage.removeItem(`${CACHE_PREFIX}${orgId}`);
  } catch {
    // ignore
  }
}
